// /home/app/apps/frontend/src/app/sections/MediaLibrary.tsx
import React, { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient, MutationFunction } from '@tanstack/react-query';

import { useSession } from '../providers/SessionProvider';
import { useProducts } from '../providers/ProductsProvider';
import { navigateInApp } from '../../utils/telegram';

import MediaGallery from '../../features/media/MediaGallery';
import {
  getFavorites,
  saveProductFavorite,
  getProducts as getOwnedAwareProducts,
} from '../../services/apiMedia';

import { useQueryParamState } from '../../hooks/useQueryParams';
import { toProductLite, Product } from '../../utils/toProductLite';

type FavoritesV2 = {
  productFavorites: string[];
  fileFavorites: Record<string, unknown>;
};

type ToggleVars = { productID: string; next: boolean };

const PAYMENT_PATH: string = (import.meta as any)?.env?.VITE_PAYMENT_PATH || '/payment';

export default function MediaLibrary() {
  const queryClient = useQueryClient();
  const { isAuthenticated, loading: sessionLoading } = useSession();

  // Public catalog (from provider)
  const {
    products: publicProducts,
    isLoading: publicLoading,
    error: publicError,
  } = useProducts();

  // Private catalog (owned-aware) for logged-in users
  const {
    data: privateProducts = [],
    isLoading: privateLoading,
    error: privateError,
  } = useQuery<Product[]>({
    queryKey: ['catalog', 'private'],
    queryFn: async () => {
      const list = await getOwnedAwareProducts();
      return Array.isArray(list) ? (list as Product[]) : [];
    },
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  // Favorites (product + per-file)
  const {
    data: favoritesV2 = { productFavorites: [], fileFavorites: {} } as FavoritesV2,
    isLoading: favLoading,
    error: favError,
  } = useQuery<FavoritesV2>({
    queryKey: ['favorites'],
    queryFn: async () => {
      const f = await getFavorites();
      return {
        productFavorites: Array.isArray((f as any)?.productFavorites)
          ? (f as any).productFavorites
          : [],
        fileFavorites: ((f as any)?.fileFavorites as FavoritesV2['fileFavorites']) || {},
      };
    },
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  // Search from ?q= (in-place URL sync, no reload)
  const [search, setSearch] = useQueryParamState('q', '');

  const listToRender: Product[] =
    (isAuthenticated ? privateProducts : (publicProducts as Product[] | undefined)) || [];

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = listToRender || [];
    if (!q) return list.map(toProductLite);
    return list
      .filter((p) => (p?.title || '').toLowerCase().includes(q))
      .map(toProductLite);
  }, [listToRender, search]);

  const productFavoritesSet = useMemo(
    () => new Set<string>(favoritesV2?.productFavorites ?? []),
    [favoritesV2]
  );

  const isLoading =
    sessionLoading || publicLoading || privateLoading || (isAuthenticated && favLoading);
  const fatalError = publicError || privateError || (isAuthenticated && favError) || null;

  const handleLoginClick = () => navigateInApp(PAYMENT_PATH);
  const onOpenProduct = (productID: string) =>
    navigateInApp(`/product/${encodeURIComponent(productID)}`);

  // Toggle favorites (optimistic)
  const mutationFn: MutationFunction<void, ToggleVars> = async ({ productID, next }) =>
    saveProductFavorite(productID, next);

  const toggleFavMutation = useMutation<void, unknown, ToggleVars, { prev?: FavoritesV2 }>({
    mutationFn,
    onMutate: async (vars: ToggleVars) => {
      const { productID, next } = vars;
      await queryClient.cancelQueries({ queryKey: ['favorites'] });

      const prev = queryClient.getQueryData<FavoritesV2>(['favorites']);
      const prevList = prev?.productFavorites || [];

      const nextList = next
        ? Array.from(new Set([...prevList, productID]))
        : prevList.filter((id: string) => id !== productID);

      queryClient.setQueryData<FavoritesV2>(['favorites'], {
        productFavorites: nextList,
        fileFavorites: prev?.fileFavorites || {},
      });

      return { prev };
    },
    onError: (_err: unknown, _vars: ToggleVars, ctx?: { prev?: FavoritesV2 }) => {
      if (ctx?.prev) queryClient.setQueryData(['favorites'], ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] }).catch(() => {});
    },
  });

  const onToggleProductFavorite = (productID: string) => {
    if (!isAuthenticated) return handleLoginClick();
    const next = !productFavoritesSet.has(productID);
    toggleFavMutation.mutate({ productID, next });
  };

  // Timestamp jumping (chips) from gallery
  const onJumpToTimestamp = (fileId: string, seconds: number | string) => {
    const productId = String(fileId).split(':')[0];
    const qp = new URLSearchParams({
      jumpFile: fileId,
      t: String(Math.max(0, parseInt(String(seconds), 10) || 0)),
    }).toString();
    navigateInApp(`/product/${encodeURIComponent(productId)}?${qp}`);
  };

  return (
    <section className="section section-media" aria-label="Media Library">
      {/* local toolbar: search */}
      <div className="app-toolbar">
        <input
          type="text"
          className="app-search"
          placeholder="Search products…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search products"
          disabled={isLoading && listToRender.length === 0}
        />
      </div>

      <MediaGallery
        className="mg-wrap"
        classes={{
          grid: 'mg-grid',
          loading: 'mg-loading',
          empty: 'mg-empty',
          card: 'mg-card',
          thumbWrap: 'mg-thumbWrap',
          thumbImg: 'mg-thumbImg',
          thumbFallback: 'mg-thumbFallback',
          ribbonOwned: 'mg-ribbonOwned',
          lockOverlay: 'mg-lockOverlay',
          lockIcon: 'mg-lockIcon',
          favBtn: 'mg-favBtn',
          cardBody: 'mg-cardBody',
          titleRow: 'mg-titleRow',
          title: 'mg-title',
          metaRow: 'mg-metaRow',
          priceChip: 'mg-priceChip',
          ownedChip: 'mg-ownedChip',
          favListWrap: 'mg-favListWrap',
        }}
        products={filteredProducts}
        productFavoritesSet={isAuthenticated ? productFavoritesSet : undefined}
        fileFavorites={isAuthenticated ? (favoritesV2.fileFavorites as any) : undefined}
        onOpenProduct={onOpenProduct}
        onToggleProductFavorite={onToggleProductFavorite}
        onJumpToTimestamp={isAuthenticated ? onJumpToTimestamp : undefined}
        isLoading={isLoading && listToRender.length === 0}
      />

      {fatalError ? (
        <p className="app-error" role="alert">
          ⚠️ {(fatalError as any)?.message || 'Something went wrong'}
        </p>
      ) : null}
    </section>
  );
}
